import type { Action, EngineEvent, GameState, MapId } from '@cc/shared';
import { io, type Socket } from 'socket.io-client';
import { create } from 'zustand';
import { play } from '../audio/sfx';

interface PublicPlayer { name: string; connected: boolean }
interface RoomMeta {
  code: string;
  status: 'waiting' | 'playing' | 'finished';
  options: { mapId: MapId; timerSeconds: 0 | 60 | 120 };
  players: PublicPlayer[];
  youAre: 0 | 1;
  goldIndex?: 0 | 1;
}

interface RoomStore {
  socket: Socket | null;
  room: RoomMeta | null;
  state: GameState | null;
  viewerIndex: 0 | 1 | null;
  goldIndex: 0 | 1 | null;
  ceremony: boolean;
  pending: boolean;
  deadline: number | null;
  emote: { index: 0 | 1; id: number; nonce: number } | null;
  rematchVotes: [boolean, boolean];
  disconnectedSince: [number | null, number | null];
  error: string | null;
  connect(): Socket;
  rejoin(code: string, token: string): void;
  join(code: string, name: string): Promise<string>;
  dismissCeremony(): void;
  flip(): void;
  action(action: Action): void;
  undo(): void;
  endTurn(): void;
  resign(): void;
  claimVictory(): void;
  emoteSend(id: number): void;
  rematch(): void;
  leave(): void;
}

function playEvents(events: EngineEvent[]) {
  for (const event of events) {
    if (event.type === 'flipStarted') play('flip');
    if (event.type === 'placed') play('place');
    if (event.type === 'paid') play('pay');
    if (event.type === 'moved') play('move');
    if (event.type === 'captured') play('capture');
    if (event.type === 'turnStarted') play('turn');
    if (event.type === 'gameOver') play('win');
  }
}

export const useRoomStore = create<RoomStore>((set, get) => ({
  socket: null,
  room: null,
  state: null,
  viewerIndex: null,
  goldIndex: null,
  ceremony: false,
  pending: false,
  deadline: null,
  emote: null,
  rematchVotes: [false, false],
  disconnectedSince: [null, null],
  error: null,
  connect: () => {
    const existing = get().socket;
    if (existing) { if (!existing.connected) existing.connect(); return existing; }
    const socket = io({ path: '/chance/socket.io', autoConnect: true, reconnection: true });
    socket.on('room:state', (room: RoomMeta) => {
      const goldIndex = room.goldIndex ?? get().goldIndex;
      const viewerIndex = goldIndex === null ? get().viewerIndex : room.youAre === goldIndex ? 0 : 1;
      set((current) => ({ room, goldIndex, viewerIndex, error: null, disconnectedSince: room.players.map((player, index) => player.connected ? null : current.disconnectedSince[index] ?? Date.now()) as [number | null, number | null] }));
    });
    socket.on('game:start', ({ state, goldIndex }: { state: GameState; goldIndex: 0 | 1; ceremonySeed: number }) => {
      const room = get().room;
      set({ state, goldIndex, viewerIndex: room ? (room.youAre === goldIndex ? 0 : 1) : null, ceremony: true, pending: false, rematchVotes: [false, false], error: null });
    });
    socket.on('game:state', ({ state, events }: { state: GameState; events: EngineEvent[] }) => {
      if (get().state && state.seq < get().state!.seq) return;
      playEvents(events);
      set({ state, pending: false, error: null });
    });
    socket.on('timer:sync', ({ deadline }: { deadline: number }) => set({ deadline }));
    socket.on('player:connection', ({ index, connected }: { index: 0 | 1; connected: boolean }) => set((current) => {
      if (!current.room) return {};
      const disconnectedSince: [number | null, number | null] = [...current.disconnectedSince];
      disconnectedSince[index] = connected ? null : Date.now();
      return { room: { ...current.room, players: current.room.players.map((player, playerIndex) => playerIndex === index ? { ...player, connected } : player) }, disconnectedSince };
    }));
    socket.on('room:emote', ({ index, id }: { index: 0 | 1; id: number }) => { play('emote'); set({ emote: { index, id, nonce: Date.now() } }); window.setTimeout(() => set({ emote: null }), 3_000); });
    socket.on('room:rematchState', ({ votes }: { votes: [boolean, boolean] }) => set({ rematchVotes: votes }));
    socket.on('error', ({ message }: { message: string }) => set({ error: message, pending: false }));
    socket.on('disconnect', () => set({ error: 'Connection lost — trying to reconnect…', pending: false }));
    set({ socket });
    return socket;
  },
  rejoin: (code, token) => { const socket = get().connect(); socket.emit('room:rejoin', { code, token }); },
  join: (code, name) => new Promise((resolve, reject) => {
    const socket = get().connect();
    socket.emit('room:join', { code, name }, (result: { ok: boolean; token?: string; error?: string }) => {
      if (result.ok && result.token) resolve(result.token);
      else reject(new Error(result.error === 'ROOM_FULL' ? 'This game is already full.' : 'Could not join that room.'));
    });
  }),
  dismissCeremony: () => set({ ceremony: false }),
  flip: () => { get().socket?.emit('game:flip', {}); set({ pending: true }); },
  action: (action) => { get().socket?.emit('game:action', { action }); set({ pending: true }); },
  undo: () => { get().socket?.emit('game:undoTurn', {}); set({ pending: true }); },
  endTurn: () => { get().socket?.emit('game:endTurn', {}); set({ pending: true }); },
  resign: () => { get().socket?.emit('game:resign', {}); set({ pending: true }); },
  claimVictory: () => { get().socket?.emit('game:claimVictory', {}); set({ pending: true }); },
  emoteSend: (id) => get().socket?.emit('room:emote', { id }),
  rematch: () => get().socket?.emit('room:rematch', {}),
  leave: () => {
    get().socket?.emit('room:leave', {});
    get().socket?.disconnect();
    set({ socket: null, room: null, state: null, viewerIndex: null, goldIndex: null, ceremony: false, pending: false, deadline: null, emote: null, rematchVotes: [false, false], disconnectedSince: [null, null], error: null });
  },
}));
